import type { SearchUsersRequest } from '../../types/api';
import type { SelectOption } from '../../components';

export interface UserAdvancedFilters {
  minAge: string;
  maxAge: string;
  minHeightId: string;
  maxHeightId: string;
  maritalStatusId: string;
  motherTongueId: string;
  religionId: string;
  sectId: string;
  jamatId: string;
  casteId: string;
  haveCasteBar: boolean;
  manglikPreference: string;
  countryId: string;
  stateId: string;
  cityId: string;
  highestDegreeId: string;
  employedInId: string;
  occupationId: string;
  incomeCurrencyId: string;
  minAnnualIncome: string;
  minWeight: string;
  maxWeight: string;
  complexionId: string;
  bloodGroupId: string;
  disabilityId: string;
  drinkingHabitId: string;
  smokingHabitId: string;
  dietId: string;
  hobbyIds: string[];
}

export const INITIAL_USER_ADVANCED_FILTERS: UserAdvancedFilters = {
  minAge: '',
  maxAge: '',
  minHeightId: '',
  maxHeightId: '',
  maritalStatusId: '',
  motherTongueId: '',
  religionId: '',
  sectId: '',
  jamatId: '',
  casteId: '',
  haveCasteBar: false,
  manglikPreference: '',
  countryId: '',
  stateId: '',
  cityId: '',
  highestDegreeId: '',
  employedInId: '',
  occupationId: '',
  incomeCurrencyId: '',
  minAnnualIncome: '',
  minWeight: '',
  maxWeight: '',
  complexionId: '',
  bloodGroupId: '',
  disabilityId: '',
  drinkingHabitId: '',
  smokingHabitId: '',
  dietId: '',
  hobbyIds: [],
};

export const MANGLIK_PREFERENCE_OPTIONS: SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'doesnt-matter', label: "Doesn't Matter" },
];

export const AGE_OPTIONS: SelectOption[] = Array.from({ length: 50 }, (_, i) => {
  const age = i + 18;
  return { value: String(age), label: String(age) };
});

const parsePositiveInt = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num <= 0) return undefined;
  return Math.floor(num);
};

const parsePositiveFloat = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num <= 0) return undefined;
  return num;
};

export const hasAdvancedFilters = (advanced: UserAdvancedFilters): boolean => {
  return (
    Boolean(advanced.minAge) ||
    Boolean(advanced.maxAge) ||
    Boolean(advanced.minHeightId) ||
    Boolean(advanced.maxHeightId) ||
    Boolean(advanced.maritalStatusId) ||
    Boolean(advanced.motherTongueId) ||
    Boolean(advanced.religionId) ||
    Boolean(advanced.sectId) ||
    Boolean(advanced.jamatId) ||
    Boolean(advanced.casteId) ||
    advanced.haveCasteBar ||
    Boolean(advanced.manglikPreference) ||
    Boolean(advanced.countryId) ||
    Boolean(advanced.stateId) ||
    Boolean(advanced.cityId) ||
    Boolean(advanced.highestDegreeId) ||
    Boolean(advanced.employedInId) ||
    Boolean(advanced.occupationId) ||
    Boolean(advanced.incomeCurrencyId) ||
    Boolean(advanced.minAnnualIncome) ||
    Boolean(advanced.minWeight) ||
    Boolean(advanced.maxWeight) ||
    Boolean(advanced.complexionId) ||
    Boolean(advanced.bloodGroupId) ||
    Boolean(advanced.disabilityId) ||
    Boolean(advanced.drinkingHabitId) ||
    Boolean(advanced.smokingHabitId) ||
    Boolean(advanced.dietId) ||
    advanced.hobbyIds.length > 0
  );
};

export const mapAdvancedFiltersToSearchRequest = (
  advanced: UserAdvancedFilters
): Partial<SearchUsersRequest> => {
  const body: Partial<SearchUsersRequest> = {};

  const minAge = parsePositiveInt(advanced.minAge);
  const maxAge = parsePositiveInt(advanced.maxAge);
  if (minAge !== undefined) body.min_age = minAge;
  if (maxAge !== undefined) body.max_age = maxAge;

  const minHeightId = parsePositiveInt(advanced.minHeightId);
  const maxHeightId = parsePositiveInt(advanced.maxHeightId);
  if (minHeightId !== undefined) body.min_height_id = minHeightId;
  if (maxHeightId !== undefined) body.max_height_id = maxHeightId;

  const maritalStatusId = parsePositiveInt(advanced.maritalStatusId);
  if (maritalStatusId !== undefined) body.maratial_status_id = maritalStatusId;

  const motherTongueId = parsePositiveInt(advanced.motherTongueId);
  if (motherTongueId !== undefined) body.mother_tongue_id = motherTongueId;

  const religionId = parsePositiveInt(advanced.religionId);
  if (religionId !== undefined) body.religion_id = religionId;

  const sectId = parsePositiveInt(advanced.sectId);
  if (sectId !== undefined) body.sect_id = sectId;

  const jamatId = parsePositiveInt(advanced.jamatId);
  if (jamatId !== undefined) body.jamat_id = jamatId;

  const casteId = parsePositiveInt(advanced.casteId);
  if (casteId !== undefined) body.caste_id = casteId;

  if (advanced.haveCasteBar) {
    body.have_caste_bar = true;
  }

  if (advanced.manglikPreference) {
    body.manglik_preference = advanced.manglikPreference;
  }

  const countryId = parsePositiveInt(advanced.countryId);
  if (countryId !== undefined) body.country_id = countryId;

  const stateId = parsePositiveInt(advanced.stateId);
  if (stateId !== undefined) body.state_id = stateId;

  const cityId = parsePositiveInt(advanced.cityId);
  if (cityId !== undefined) body.city_id = cityId;

  const highestDegreeId = parsePositiveInt(advanced.highestDegreeId);
  if (highestDegreeId !== undefined) body.heighest_degree_id = highestDegreeId;

  const employedInId = parsePositiveInt(advanced.employedInId);
  if (employedInId !== undefined) body.employed_in_id = employedInId;

  const occupationId = parsePositiveInt(advanced.occupationId);
  if (occupationId !== undefined) body.occupation_id = occupationId;

  const incomeCurrencyId = parsePositiveInt(advanced.incomeCurrencyId);
  if (incomeCurrencyId !== undefined) body.income_currency_id = incomeCurrencyId;

  const minAnnualIncome = parsePositiveInt(advanced.minAnnualIncome);
  if (minAnnualIncome !== undefined) body.min_annual_income = minAnnualIncome;

  const minWeight = parsePositiveFloat(advanced.minWeight);
  const maxWeight = parsePositiveFloat(advanced.maxWeight);
  if (minWeight !== undefined) body.min_weight = minWeight;
  if (maxWeight !== undefined) body.max_weight = maxWeight;

  const complexionId = parsePositiveInt(advanced.complexionId);
  if (complexionId !== undefined) body.complexion_id = complexionId;

  const bloodGroupId = parsePositiveInt(advanced.bloodGroupId);
  if (bloodGroupId !== undefined) body.blood_group_id = bloodGroupId;

  const disabilityId = parsePositiveInt(advanced.disabilityId);
  if (disabilityId !== undefined) body.disability_id = disabilityId;

  const drinkingHabitId = parsePositiveInt(advanced.drinkingHabitId);
  if (drinkingHabitId !== undefined) body.drinking_habit_id = drinkingHabitId;

  const smokingHabitId = parsePositiveInt(advanced.smokingHabitId);
  if (smokingHabitId !== undefined) body.smoking_habit_id = smokingHabitId;

  const dietId = parsePositiveInt(advanced.dietId);
  if (dietId !== undefined) body.diet_id = dietId;

  if (advanced.hobbyIds.length > 0) {
    body.hobby_id = advanced.hobbyIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }

  return body;
};
